/* Meta Pixel + LinkedIn Insight Tag - ad conversion tracking for paid FB/IG/
   LinkedIn campaigns. IDs are hardcoded (not env-gated like the marketing
   analytics stack): growth needs these live in every environment without
   depending on a .env key being maintained, and hardcoding is what lets
   Pixel Helper / Meta Test Events verify the install end-to-end, including
   from local dev.

   Scope: the pre-auth surface only (WedgeAuthGate's logged-out branch,
   which renders WedgeLanding - "the wedge landing" ads point at per
   docs/wedge/specs/login-experience-rebuild.md - and AuthCallbackWedge,
   where the backend confirms a brand-new signup via ?fresh=1). NOT loaded
   inside the authenticated product: the PDF's goal is tracking ad-driven
   visits + the signup conversion for retargeting/lookalike audiences, not
   in-product engagement from already-converted reps.

   NATIVE iOS: never loaded. See isNativeShell() below; every export is a
   no-op inside the Capacitor shell so no tracker script, stub, or event
   ever runs in the App Store build. */

import { isNativeIOS } from './native/nativeBridge';

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
    };
    _fbq?: unknown;
    lintrk?: ((...args: unknown[]) => void) & { q?: unknown[] };
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
  }
}

const META_PIXEL_ID = '1932891581447397';
const LINKEDIN_PARTNER_ID = '9613228';

/* Native iOS must not load ad trackers, period. The Capacitor shell has no
   ATT consent flow (no NSUserTrackingUsageDescription, no
   PrivacyInfo.xcprivacy exists in the repo), so injecting the Meta Pixel or
   LinkedIn Insight Tag inside the app is an App Review 5.1.2 / ATT
   rejection. Gating the whole module out on native removes the ATT question
   entirely. Web behavior is unchanged: isNativeIOS() is false in every
   browser, so this returns false there and the try/catch keeps any bridge
   surprise from ever blocking web tracking. */
function isNativeShell(): boolean {
  try {
    return isNativeIOS();
  } catch {
    return false;
  }
}

/* 2026-10-01 (48h audit) · the pixels send the full page URL (Meta sends it
   as `dl`). On the pre-auth redeem pages that URL carries a live single-use
   sign-in token (?magic=, ?reset=) or an email address (?code=&email=), and
   the box access log showed ?magic= links reaching this page. A page whose
   query holds any of these keys loads no tracker and fires no event. */
const CREDENTIAL_PARAMS = ['magic', 'reset', 'token', 'code', 'email'];
export function urlCarriesCredential(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    return CREDENTIAL_PARAMS.some((k) => params.has(k));
  } catch {
    return true;
  }
}

function initMetaPixel(): void {
  if (window.fbq) return;
  let n: Window['fbq'];
  n = window.fbq = ((...args: unknown[]) => {
    n!.callMethod ? n!.callMethod.apply(n, args) : n!.queue!.push(args);
  }) as Window['fbq'];
  if (!window._fbq) window._fbq = n;
  n.queue = [];
  n.loaded = true;
  n.version = '2.0';
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const s = document.getElementsByTagName('script')[0];
  s.parentNode?.insertBefore(t, s);
  window.fbq('init', META_PIXEL_ID);
}

function initLinkedInInsightTag(): void {
  if (window.lintrk) return;
  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  let lintrk: Window['lintrk'];
  lintrk = window.lintrk = ((...args: unknown[]) => {
    lintrk!.q!.push(args);
  }) as Window['lintrk'];
  lintrk.q = [];
  const b = document.createElement('script');
  b.type = 'text/javascript';
  b.async = true;
  b.src = 'https://snap.licdn.com/li/lms-analytics/insight.min.js';
  const s = document.getElementsByTagName('script')[0];
  s.parentNode?.insertBefore(b, s);
}

// Fires every platform's PageView-equivalent.
export function trackPageView(): void {
  if (typeof window === 'undefined' || isNativeShell() || urlCarriesCredential()) return;
  if (typeof window.fbq === 'function') window.fbq('track', 'PageView');
  // LinkedIn conversions are wired in Campaign Manager against a page-load
  // URL - no per-pageview lintrk() call needed for the basic setup.
}

// Base install, called once per pre-auth page load. Orchestrates every
// platform's base tag - add a new platform by writing its own initX()
// above and calling it here, so trackPageView()/trackSignupLead() and any
// future track*() export pick it up automatically.
export function initTrackingTags(): void {
  if (typeof window === 'undefined') return;
  /* Guard BEFORE any init: on native iOS neither pixel script is injected
     and no fbq/lintrk stub is created (App Review 5.1.2 / ATT). */
  if (isNativeShell()) return;
  if (urlCarriesCredential()) return;
  initMetaPixel();
  initLinkedInInsightTag();
  trackPageView();
}

// Fired once, at the true signup moment (AuthCallbackWedge.tsx, when the
// backend's ?fresh=1 confirms a brand-new account was just created).
export function trackSignupLead(): void {
  if (isNativeShell() || urlCarriesCredential()) return;
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'Lead');
  }
}
